import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { requestAccountDeletion } from "../services/api";

const AccountDeletionRequest: React.FC = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const isValidEmail = (value: string) => {
    // Validação simples de e-mail
    return /.+@.+\..+/.test(value.trim());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isValidEmail(email)) {
      setError("Informe um e-mail válido.");
      return;
    }
    setLoading(true);
    try {
      await requestAccountDeletion(email.trim());
      navigate("/deletar-conta/solicitado", {
        replace: true,
        state: { email: email.trim() },
      });
      setSubmitted(true);
    } catch (err: any) {
      console.error("Erro ao solicitar exclusão de conta:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Não foi possível enviar sua solicitação agora. Tente novamente em instantes.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Typography variant="h4" gutterBottom>
        Solicitar exclusão de conta
      </Typography>
      <Typography variant="body1" gutterBottom>
        A exclusão é permanente e acontece imediatamente após você confirmar o
        pedido por e-mail. Antes disso, confirmamos sua identidade para manter
        sua conta segura.
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
        <TextField
          type="email"
          required
          fullWidth
          label="E-mail da conta"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={Boolean(email) && !isValidEmail(email)}
          helperText={
            Boolean(email) && !isValidEmail(email)
              ? "E-mail inválido"
              : "Você receberá um e-mail com um link para confirmar. Após a confirmação, a exclusão é imediata e irreversível."
          }
          disabled={loading || submitted}
        />
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={!email || !isValidEmail(email) || loading || submitted}
        >
          {loading ? (
            <>
              <CircularProgress size={18} sx={{ mr: 1 }} /> Enviando...
            </>
          ) : (
            "Solicitar exclusão"
          )}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} role="alert">
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        Observação: em alguns casos podemos reter registros mínimos quando
        exigido por lei, auditoria ou prevenção a fraudes. Esses registros não
        permitem identificar você diretamente. Para detalhes completos sobre
        exclusão e retenção de dados, consulte nossa{" "}
        <Link component={RouterLink} to="/politica-de-privacidade">
          Política de Privacidade
        </Link>{" "}
        e{" "}
        <Link component={RouterLink} to="/termos-de-uso">
          Termos de Uso
        </Link>
        .
      </Typography>
    </Container>
  );
};

export default AccountDeletionRequest;
